/**
 * @file Alerte non-bloquante pour les erreurs de domaine email détectées à la saisie.
 * @module packages.common.components.EmailSuggestionAlert
 *
 * Affiche un bandeau d'avertissement en cas de typo détectée dans le domaine
 * (ex: "fmail.com" → suggère "gmail.com") ou de domaine sans enregistrements MX.
 * L'alerte est toujours non-bloquante : l'utilisateur peut accepter ou ignorer.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import {InlineNotice} from 'packages/common/components/inline-notice';

/**
 * Affiche un avertissement de typo ou de domaine invalide sous un champ email.
 *
 * @param {Object} props
 * @param {Object|null} props.suggestion - Suggestion de correction `{ original, corrected }`
 * @param {boolean} props.domainError - Vrai si le domaine n'a pas d'enregistrements MX
 * @param {string} props.email - Valeur actuelle du champ email (pour afficher l'email corrigé complet)
 * @param {Function} props.onAccept - Appelé avec le domaine corrigé quand l'utilisateur accepte
 * @param {Function} props.onDismiss - Appelé quand l'utilisateur ferme l'alerte
 * @param {Object} [props.sx] - Marges propres à l'emplacement (par défaut, un léger écart sous le champ)
 * @return {React.Element|null}
 *
 * @example
 * <EmailSuggestionAlert
 *   suggestion={suggestion}
 *   domainError={domainError}
 *   email={emailValue}
 *   onAccept={(correctedDomain) => setValue('email', buildCorrectedEmail(email, correctedDomain))}
 *   onDismiss={dismiss}
 * />
 */
export function EmailSuggestionAlert({suggestion, domainError, email, onAccept, onDismiss, sx = {mt: 1}}) {
  const visible = Boolean(suggestion || domainError);
  const correctedEmail = suggestion && email ?
    `${email.split('@')[0]}@${suggestion.corrected}` :
    suggestion?.corrected;

  return (
    <Collapse in={visible} unmountOnExit>
      <InlineNotice
        sx={sx}
        onClose={onDismiss}
        action={suggestion && (
          <Button
            size="small"
            color="warning"
            variant="text"
            onClick={() => onAccept(suggestion.corrected)}
            sx={{display: 'block', fontWeight: 600, p: 0, mt: 0.5, minWidth: 0, textTransform: 'none'}}
          >
            Utiliser {suggestion.corrected}
          </Button>
        )}
      >
        {suggestion ?
          <>Vouliez-vous écrire <strong>{correctedEmail}</strong> ?</> :
          'Ce domaine email semble invalide. Ignorez ce message si votre adresse est correcte.'}
      </InlineNotice>
    </Collapse>
  );
}

EmailSuggestionAlert.propTypes = {
  suggestion: PropTypes.shape({
    original: PropTypes.string.isRequired,
    corrected: PropTypes.string.isRequired,
  }),
  domainError: PropTypes.bool,
  email: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  sx: PropTypes.object,
};
